/**
 * Registro centralizado do Service Worker (dev + prod).
 *
 * Importado como side-effect em main.tsx, ANTES do React montar,
 * para que o Chrome avalie a instalabilidade do PWA o mais cedo possível.
 *
 * Exporta:
 * - getSWRegistration() — promise do registro (compat)
 * - onSWReady(cb)       — dispara quando SW está ativo (replay se já ativo)
 * - onUpdateFound(cb)   — dispara quando novo SW fica em waiting (replay se já waiting)
 */

const isBrowser =
  typeof navigator !== 'undefined' && typeof window !== 'undefined';

const hasServiceWorker = isBrowser && 'serviceWorker' in navigator;

const isDev = import.meta.env.DEV;
const swUrl = isDev ? '/dev-sw.js?dev-sw' : '/sw.js';
const swType: WorkerType = isDev ? 'module' : 'classic';

// --- Registro ---

let registrationError: unknown = null;

const registrationPromise: Promise<ServiceWorkerRegistration | null> =
  hasServiceWorker
    ? navigator.serviceWorker
        .register(swUrl, { scope: '/', type: swType })
        .catch((err: unknown) => {
          registrationError = err;
          return null;
        })
    : Promise.resolve(null);

// --- Callbacks com semântica de replay ---

type Callback = () => void;

const readyCallbacks: Callback[] = [];
let swIsReady = false;

const updateCallbacks: Callback[] = [];
let updateAlreadyFound = false;

// Inicializar listeners assim que o registro resolver
registrationPromise.then((registration) => {
  if (!registration) return;

  // --- SW Ready ---
  if (registration.active) {
    swIsReady = true;
    readyCallbacks.forEach((cb) => cb());
    readyCallbacks.length = 0;
  } else {
    // Esperar o SW ficar ativo
    const waitForActive = (sw: ServiceWorker) => {
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated' && !swIsReady) {
          swIsReady = true;
          readyCallbacks.forEach((cb) => cb());
          readyCallbacks.length = 0;
        }
      });
    };
    if (registration.installing) waitForActive(registration.installing);
    else if (registration.waiting) waitForActive(registration.waiting);
  }

  // --- Update Found ---
  if (registration.waiting && navigator.serviceWorker.controller) {
    // Já há um SW waiting (update já aconteceu antes do registro dos callbacks)
    updateAlreadyFound = true;
    updateCallbacks.forEach((cb) => cb());
    updateCallbacks.length = 0;
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;

    installing.addEventListener(
      'statechange',
      () => {
        if (
          installing.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          updateAlreadyFound = true;
          updateCallbacks.forEach((cb) => cb());
          updateCallbacks.length = 0;
        }
      },
      { once: true },
    );
  });
});

// --- API pública ---

/**
 * Retorna a promise do registro do SW (ou null se indisponível).
 */
export function getSWRegistration(): Promise<ServiceWorkerRegistration | null> {
  return registrationPromise;
}

/**
 * Retorna o erro de registro do SW, se houver.
 * Útil para diagnóstico via console remoto (Chrome DevTools).
 */
export function getSWRegistrationError(): unknown {
  return registrationError;
}

/**
 * Registra callback que dispara quando o SW está ativo.
 * Se já está ativo, dispara imediatamente (microtask).
 * Retorna função de cleanup.
 */
export function onSWReady(cb: Callback): () => void {
  if (swIsReady) {
    queueMicrotask(cb);
    return () => {};
  }

  readyCallbacks.push(cb);
  return () => {
    const idx = readyCallbacks.indexOf(cb);
    if (idx >= 0) readyCallbacks.splice(idx, 1);
  };
}

/**
 * Registra callback que dispara quando um novo SW fica em waiting.
 * Se já há um waiting, dispara imediatamente (microtask).
 * Retorna função de cleanup.
 */
export function onUpdateFound(cb: Callback): () => void {
  if (updateAlreadyFound) {
    queueMicrotask(cb);
    return () => {};
  }

  updateCallbacks.push(cb);
  return () => {
    const idx = updateCallbacks.indexOf(cb);
    if (idx >= 0) updateCallbacks.splice(idx, 1);
  };
}
